import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiInformationOutline,
  mdiClose,
  mdiCloseCircle,
  mdiCloseCircleOutline,
  mdiImagePlusOutline,
  mdiPencilOutline,
} from "@mdi/js";
import { Images } from "react-bootstrap-icons";
import FsLightbox from "fslightbox-react";
import { FILE_SIZE_LIMITS } from "../../constants/global-constants";
import {
  ACCEPTED_IMAGE_TYPES,
  CONCERN_LEVEL_OPTIONS,
} from "./editItemFormConstants";
import EditPhotoMarkup, { buildAnnotations } from "./editPhotoMarkup";

const isIssue = (type) => type === "Issue";

const AddNewItemModal = ({
  show,
  type,
  onCancel,
  onSubmit,
  isSubmitting = false,
}) => {
  // Each photo stays in-memory until AI Analyze (no immediate save):
  //   { id, baseFile, arrows, annotatedFile, imageSize, previewUrl }
  // The base file is always preserved; markup produces a separate baked
  // `annotatedFile` + `arrows`, both sent to async-generate so the backend
  // stores them apart and the annotation stays editable later.
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  const [concernLevel, setConcernLevel] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // { id, baseUrl, arrows } for the photo currently open in the markup editor.
  const [markupTarget, setMarkupTarget] = useState(null);
  const [lightbox, setLightbox] = useState({ toggler: false, index: 0 });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setFiles([]);
      setNote("");
      setConcernLevel(null);
      setValidationError(null);
      setIsDragOver(false);
      setMarkupTarget((cur) => {
        if (cur?.baseUrl) URL.revokeObjectURL(cur.baseUrl);
        return null;
      });
    }
  }, [show]);

  useEffect(() => {
    const urls = files.map((f) => f.previewUrl);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const addFiles = (incoming) => {
    const maxSize = FILE_SIZE_LIMITS.IMAGE_MAX_SIZE;
    const accepted = ACCEPTED_IMAGE_TYPES.split(",");
    const next = [];
    let oversized = 0;
    let invalidType = 0;
    for (const file of incoming) {
      if (!accepted.includes(file.type)) {
        invalidType += 1;
        continue;
      }
      if (file.size > maxSize) {
        oversized += 1;
        continue;
      }
      next.push({
        id: `${file.name}-${file.size}-${file.lastModified}`,
        baseFile: file,
        arrows: [],
        annotatedFile: null,
        imageSize: null,
        previewUrl: URL.createObjectURL(file),
      });
    }
    if (next.length) {
      setFiles((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...next.filter((n) => !seen.has(n.id))];
      });
    }
    if (oversized > 0) {
      const noun = oversized === 1 ? "photo is" : "photos are";
      setValidationError(`${oversized} ${noun} over 10MB and was not added.`);
    } else if (invalidType > 0) {
      const noun = invalidType === 1 ? "file is" : "files are";
      setValidationError(
        `${invalidType} ${noun} not a supported image (JPEG, PNG, WebP).`
      );
    } else if (next.length) {
      setValidationError(null);
    }
  };

  const handleFileInput = (event) => {
    const list = Array.from(event.target.files || []);
    addFiles(list);
    event.target.value = "";
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    const list = Array.from(event.dataTransfer?.files || []);
    addFiles(list);
  };

  const handleRemoveFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Open the markup editor on the photo's clean base file (in-memory).
  const handleOpenMarkup = (photo) => {
    setMarkupTarget({
      id: photo.id,
      baseUrl: URL.createObjectURL(photo.baseFile),
      arrows: photo.arrows,
    });
  };

  const handleCloseMarkup = () => {
    setMarkupTarget((cur) => {
      if (cur?.baseUrl) URL.revokeObjectURL(cur.baseUrl);
      return null;
    });
  };

  // Markup applied locally only — keep the base file untouched and store the
  // baked annotated file + arrows separately (both submitted to async-generate
  // so the backend stores them apart). Clearing reverts to base. The old
  // previewUrl is revoked by the files-change effect once the new one loads.
  const handleMarkupComplete = (arrows, blob, imageSize) => {
    const targetId = markupTarget?.id;
    if (!targetId) return;
    setFiles((prev) =>
      prev.map((f) => {
        if (f.id !== targetId) return f;
        const annotatedFile = blob
          ? new File([blob], f.baseFile.name, {
              type: blob.type || f.baseFile.type,
            })
          : null;
        return {
          ...f,
          arrows,
          annotatedFile,
          imageSize: imageSize || f.imageSize,
          previewUrl: URL.createObjectURL(annotatedFile || f.baseFile),
        };
      })
    );
  };

  const handleView = (index) => {
    setLightbox((prev) => ({ toggler: !prev.toggler, index }));
  };

  const handleAnalyze = () => {
    const isLimit = type === "Limit";
    const hasNote = note.trim().length > 0;
    if (isLimit) {
      if (!files.length && !hasNote) {
        setValidationError("Add at least 1 photo or 1 note");
        return;
      }
    } else if (!files.length) {
      setValidationError("Add at least 1 photo");
      return;
    }
    if (isIssue(type) && concernLevel === null) {
      setValidationError("Select a concerning level");
      return;
    }
    onSubmit({
      images: files.map((f) => f.baseFile),
      annotatedImages: files.map((f) => f.annotatedFile || null),
      annotations: files.map((f) =>
        f.annotatedFile ? buildAnnotations(f.arrows, f.imageSize) : null
      ),
      descriptionHint: note.trim() || undefined,
      concernLevel: isIssue(type) ? concernLevel : undefined,
    });
  };

  const showConcernLevel = isIssue(type);
  const title = type ? `Add New ${type}` : "Add New Item";
  const hasFiles = files.length > 0;

  return (
    <Modal
      show={show}
      onHide={onCancel}
      centered
      scrollable
      backdrop="static"
      className="webedit-bottom-modal"
      dialogClassName="add-new-item-modal"
    >
      <Modal.Header className="add-new-item-modal-header">
        <Modal.Title>{title}</Modal.Title>
        <Button
          variant="link"
          onClick={onCancel}
          className="add-new-item-close-btn"
          aria-label="Close"
        >
          <Icon path={mdiClose} size={1} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <div className="add-new-item-hint">
          <Icon path={mdiInformationOutline} size={0.667} />
          <span>Add short note or annotation for more precise analysis.</span>
        </div>

        <div
          className={`add-new-item-dropzone ${isDragOver ? "drag-over" : ""} ${hasFiles ? "has-files" : ""}`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {hasFiles ? (
            <div className="add-new-item-thumbnails">
              {files.map((photo, index) => (
                <div key={photo.id} className="add-new-item-thumbnail">
                  <img src={photo.previewUrl} alt="uploaded preview" />
                  <div className="edit-image-hover-overlay">
                    <button
                      type="button"
                      className="edit-image-close-btn"
                      onClick={() => handleRemoveFile(photo.id)}
                      aria-label="Delete photo"
                    >
                      <Icon path={mdiCloseCircleOutline} size={1} />
                    </button>
                    <div className="edit-image-hover-actions">
                      <Button
                        size="sm"
                        className="edit-image-markup-btn"
                        onClick={() => handleOpenMarkup(photo)}
                      >
                        <Icon path={mdiPencilOutline} size={0.667} />
                        Markup
                      </Button>
                      <Button
                        size="sm"
                        className="edit-image-view-btn"
                        onClick={() => handleView(index)}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="add-new-item-dropzone-empty">
              <Images size={48} className="add-new-item-dropzone-icon" />
              <div className="add-new-item-dropzone-text">
                <span>Drop Photos or</span>
                <button
                  type="button"
                  className="add-new-item-browse-btn"
                  onClick={handleBrowseClick}
                >
                  Browse
                </button>
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES}
            multiple
            onChange={handleFileInput}
            style={{ display: "none" }}
          />
        </div>

        {hasFiles && (
          <div className="add-new-item-add-more">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleBrowseClick}
              className="add-new-item-add-more-btn"
            >
              <Icon path={mdiImagePlusOutline} size={0.833} /> Add Photo
            </Button>
          </div>
        )}

        <div className="add-new-item-field">
          <div className="add-new-item-field-label">Note</div>
          <Form.Control
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="add-new-item-note-input"
          />
        </div>

        {showConcernLevel && (
          <div className="add-new-item-field">
            <div className="add-new-item-field-label">
              Concerning Level
              <span className="add-new-item-required">*</span>
            </div>
            <div className="add-new-item-concern-row">
              {CONCERN_LEVEL_OPTIONS.map(({ value, label, iconSrc }) => (
                <label
                  key={value}
                  className="add-new-item-concern-option"
                  htmlFor={`concern-level-${value}`}
                >
                  <input
                    type="radio"
                    id={`concern-level-${value}`}
                    name="concernLevel"
                    className="add-new-item-radio-input"
                    checked={concernLevel === value}
                    onChange={() => setConcernLevel(value)}
                  />
                  <span className="add-new-item-concern-label-text">
                    {label}
                  </span>
                  <img
                    src={iconSrc}
                    alt=""
                    className="add-new-item-concern-icon"
                  />
                </label>
              ))}
            </div>
          </div>
        )}

        {validationError && (
          <div className="add-new-item-validation-error">
            <Icon path={mdiCloseCircle} size={0.667} /> {validationError}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="add-new-item-modal-footer">
        <Button
          variant="outline-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleAnalyze}
          disabled={isSubmitting}
          className="add-new-item-analyze-btn"
        >
          {isSubmitting ? "Submitting..." : "AI Analyze"}
        </Button>
      </Modal.Footer>

      {markupTarget && (
        <EditPhotoMarkup
          show
          localImageUrl={markupTarget.baseUrl}
          localArrows={markupTarget.arrows}
          onLocalComplete={handleMarkupComplete}
          onClose={handleCloseMarkup}
        />
      )}

      <FsLightbox
        toggler={lightbox.toggler}
        sources={files.map((f) => f.previewUrl)}
        sourceIndex={lightbox.index}
      />
    </Modal>
  );
};

export default AddNewItemModal;
